"use client"

import { fetchCareers } from "@/(admin)/modules/career/services/career"
import { CAREER_FORM } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import getDomains from "@/services/domain-services"
import { CareerFormData, MediaData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useState } from "react"
import careerFormService from "../services/careerFormService"

export const useUpsertCareerForm = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()

  const id = searchParams.get("id")
  const isEditMode = useMemo(() => Boolean(id), [id])

  const { isLoading, getCareerFormById, createCareerForm, updateCareerForm } = careerFormService()

  const [domainOptions, setDomainOptions] = useState<{ label: string; value: string }[]>([])
  const [careerOptions, setCareerOptions] = useState<{ label: string; value: string }[]>([])
  const [selectedCareerOption, setSelectedCareerOption] = useState<{ label: string; value: string } | null>(null)
  const [selectedCVMedia, setSelectedCVMedia] = useState<MediaData[]>([])
  const [status, setStatus] = useState<string>("Draft")
  const [isFormReady, setIsFormReady] = useState(false)
  const getNormalizedSnapshot = useCallback(() => {
    return {
      ...normalizeFormData(form.getFieldsValue(true)),
      status,
      selectedCVMedia: selectedCVMedia.length > 0 ? selectedCVMedia.map(m => m.id) : null,
    };
  }, [form, status, selectedCVMedia])

  const { isChange, checkChange, commitSnapshot, message, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });


  useEffect(() => {
    checkChange();
  }, [status, selectedCVMedia, checkChange]);

  useEffect(() => {
    getDomains().then((res: any[]) => {
      const options =
        res?.map(item => ({
          label: item.attributes?.name ?? "",
          value: item.attributes?.domain ?? item.attributes?.name ?? "",
        })) || []
      setDomainOptions(options)
    })

    fetchCareers({
      locale: "vi",
      publicationState: "published",
    }).then(res => {
      const list = res?.data ?? []
      const options = list.map((item: any) => ({
        label: item.attributes?.position || item.attributes?.name || `#${item.id}`,
        value: String(item.id),
      }))
      setCareerOptions(options)
    })
  }, [])

  const mergedCareerOptions = useMemo(() => {
    if (!selectedCareerOption) return careerOptions
    const existed = careerOptions.some(option => option.value === selectedCareerOption.value)
    if (existed) return careerOptions
    return [selectedCareerOption, ...careerOptions]
  }, [careerOptions, selectedCareerOption])

  useEffect(() => {
    if (isEditMode && id) {
      getCareerFormById(id as string).then(res => {
        const data = res?.data?.attributes || res?.attributes
        if (data) {
          const careerEntity = data.career?.data || data.career
          const careerId = careerEntity?.id ? String(careerEntity.id) : undefined
          const careerLabel =
            careerEntity?.attributes?.position ||
            careerEntity?.attributes?.name ||
            (careerId ? `#${careerId}` : "")

          if (careerId) {
            setSelectedCareerOption({
              value: careerId,
              label: careerLabel,
            })
          }

          form.setFieldsValue({
            fullname: data.fullname,
            phone: data.phone,
            email: data.email,
            selfInstruction: data.selfInstruction,
            domain: data.domain,
            career: careerId,
            cv: Array.isArray(data.cv?.data)
              ? data.cv.data.map((item: any) => item.id)
              : Array.isArray(data.cv)
                ? data.cv
                : undefined,
            resolved: data.resolved ?? false,
          })

          setStatus(data.publishedAt ? "Published" : "Draft")

          if (Array.isArray(data.cv?.data) && data.cv.data.length > 0) {
            const medias: MediaData[] = data.cv.data.map((item: any) => {
              return {
                id: item.id,
                attributes: item.attributes,
              } as unknown as MediaData
            })
            setSelectedCVMedia(medias)
          }
          setIsFormReady(true);
        }
      })
    } else {
      form.setFieldValue("resolved", false)
      setIsFormReady(true);
    }
  }, [id, isEditMode, form, getCareerFormById])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const onFinish = async (values: CareerFormData) => {
    const payload = {
      data: {
        ...values,
        publishedAt: status === "Published" ? new Date().toISOString() : null,
      }
    }
    if (isEditMode) {
      await updateCareerForm(id as string, payload)
      commitSnapshot();
      message.success(t("Cập nhật đơn tuyển dụng thành công"))

    } else {
      await createCareerForm(payload)
      commitSnapshot();
      message.success(t("Tạo đơn tuyển dụng thành công"))
      const targetPath = createPath(CAREER_FORM.LIST.PATH, searchParams)
      router.push(targetPath)
    }
  }

  const handleBack = useCallback(() => {
    const targetPath = createPath(CAREER_FORM.LIST.PATH, searchParams)
    guardCancel(targetPath)
  }, [router, searchParams, guardCancel])


  return {
    form,
    isEditMode,
    submitting: isLoading,
    domainOptions,
    careerOptions: mergedCareerOptions,
    onFinish,
    selectedCVMedia,
    setSelectedCVMedia,
    status,
    setStatus,
    handleFormChange: checkChange,
    handleBack,
    isChange
  }
}
